import React from "react";
import { useLabels, FilterType } from "../context/LabelsContext";
import { useReleases } from "../context/ReleasesContext";
import LabelCard from "../components/LabelCard";
import SearchBar from "../components/SearchBar";
import { Link } from "react-router-dom";

export const PaginationControls: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  size?: 'sm' | 'md';
}> = ({ currentPage, totalPages, onPageChange, size = 'md' }) => {
  const buttonClass = size === 'sm' 
    ? 'px-2 py-1 text-sm'
    : 'px-3 py-1';
  
  const textClass = size === 'sm'
    ? 'text-sm'
    : 'text-base';

  return (
    <div className="flex items-center gap-3">
      <div className="flex gap-2">
        <button
          className={`${buttonClass} text-gray-600 bg-gray-50 hover:bg-gray-100 rounded transition-colors duration-200 disabled:opacity-40 disabled:hover:bg-gray-50`}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ←
        </button>
        <button
          className={`${buttonClass} text-gray-600 bg-gray-50 hover:bg-gray-100 rounded transition-colors duration-200 disabled:opacity-40 disabled:hover:bg-gray-50`}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          →
        </button>
      </div>
      <span className={`${textClass} text-gray-600 font-medium`}>
        Page {currentPage} of {totalPages}
      </span>
    </div>
  );
};

// Tab component for the Collection page
const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-base font-medium rounded-lg transition-colors duration-200 ${
        active 
          ? 'bg-violet-600 text-white' 
          : 'text-gray-700 hover:bg-violet-100'
      }`}
    >
      {children}
    </button>
  );
};

const Collection: React.FC = () => {
  const { 
    paginatedLabels, 
    setLabelPage, 
    isLoading,
    error: labelsError,
    filterType,
    setFilterType
  } = useLabels();
  
  const {
    error: releasesError
  } = useReleases();
  
  const { data: labels, currentPage, totalPages } = paginatedLabels;
  const error = labelsError || releasesError;

  const handleFilterChange = (newFilter: FilterType) => {
    if (newFilter !== filterType) {
      setFilterType(newFilter);
    }
  };

  return (
    <div className="min-h-screen">
      <main className="pt-16 pb-6 px-6 max-w-[1080px] mx-auto">
        <div className="mb-8 border-b border-violet-300 pb-4">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Collection</h1>
          <p className="text-gray-700">Browse all the labels you follow</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex space-x-2">
          <TabButton 
            active={filterType === 'unchecked'} 
            onClick={() => handleFilterChange('unchecked')}
          >
            To Listen
          </TabButton>
          <TabButton 
            active={filterType === 'checked'} 
            onClick={() => handleFilterChange('checked')}
          >
            All Listened
          </TabButton>
        </div>
        
        <div className="mb-8">
          <SearchBar />
        </div>
        
        <div className="flex justify-start mb-4">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setLabelPage}
          />
        </div>
        
        {error && (
          <div className="text-red-600 mb-4">{error}</div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
          </div>
        ) : labels.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-600 text-base sm:text-lg">
              {filterType === 'checked' 
                ? "You don't have any labels with all releases listened to yet."
                : "Start following labels in the Labels page to see content on this page!"}
            </p>
            <Link 
              to="/labels"
              className="mt-4 inline-block px-5 sm:px-6 py-2.5 sm:py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors text-sm sm:text-base"
            >
              Go to Labels
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4 sm:space-y-8 mb-6 sm:mb-8">
              {labels.map((label) => (
                <LabelCard
                  key={label.label_url}
                  label={label}
                />
              ))}
            </div>

            <div className="flex justify-start mt-4">
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setLabelPage}
              />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default Collection;
